import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { AsyncSelect, Select } from "@commonsku/styles";

import { getCompanyData } from "../selectors";
import { getTaxDropdown } from "../selectors/dropdowns";
import Tax from "../models/Tax";
import { useAvalara } from "./avalara/hooks";
import { formatPercent, oauth } from "../utils";

interface Option {
  value: string;
  id?: string;
  taxId: string;
  label: string;
  percent: number;
}

type Options = Array<Option>;

const AVALARA_TAX_OPTION: Option = {
  value: Tax.avalara_tax_id,
  taxId: Tax.avalara_tax_id,
  label: "Avalara",
  percent: 0,
};

const getTaxOptions = createSelector(
  getTaxDropdown,
  options => options.map(
    o => ({
      value: o.tax_id,
      taxId: o.tax_id,
      label: !!o.zip2tax_id ? o.description : o.label,
      percent: parseFloat(o.percent),
    })
  )
);

/*
  Sort order:
    Avalara (there is only one of these)
    Tax Included (there is only one of these)
    Manual Taxes (Default tax exempt first, then alphabetically by label)
    Zip2Tax Taxes (Alphabetically by description)
*/
function sortTaxes(a: Option, b: Option): number {
  if (a.value === Tax.avalara_tax_id) {
    return -1;
  }
  if (b.value === Tax.avalara_tax_id) {
    return 1;
  }
  if (a.label === "E") {
    return -1;
  }
  if (b.label === "E") {
    return 1;
  }
  return a.label.localeCompare(b.label);
}

function formatOptionLabel(option: Option): React.ReactNode {
  return `${option.label} (${formatPercent(option.percent)})`;
}

interface TaxAmountSelectProps {
  value?: Options,
  onChange: (value: Options) => void;
  isAvalara: boolean;
  isZip2Tax: boolean;
  disabled?: boolean;
}

function TaxAmountSelect({
  value,
  onChange,
  isAvalara,
  isZip2Tax,
  disabled = false,
}: TaxAmountSelectProps
) {
  const dispatch = useDispatch();
  const baseOptions = useSelector(getTaxOptions);
  const { isConnected, avalara_testing } = useAvalara();

  const includeAvalaraTax = isConnected() && !avalara_testing && isAvalara;

  const options = useMemo(
    () => {
      const options = (isAvalara || isZip2Tax ? baseOptions.filter(o => o.value === value) : baseOptions);
      if (includeAvalaraTax) {
        options.push(AVALARA_TAX_OPTION);
      };
      options.sort(sortTaxes);
      return options as Option[];
    },
    [baseOptions, value, isAvalara, isZip2Tax]
  );

  const loadTaxes = useCallback(
    async (search: string, callback: (options: Option[]) => void) => {
      const result = await oauth("GET", "tax", { zip2tax: search });
      const options = result.json.taxes.map(
        t => ({
          value: t.tax_id,
          taxId: t.tax_id,
          label: `${t.description} (${parseFloat(t.percent)}%)`,
          percent: t.percent,
        })
      ) as Option[];
      if (isAvalara) {
        options.push(AVALARA_TAX_OPTION);
      };
      options.sort(sortTaxes);

      return options;
    },
    [isAvalara]
  );

  if (isZip2Tax && !disabled) {
    return (
      <AsyncSelect
        inPopup
        isMulti
        cacheOptions
        value={value}
        defaultOptions={options}
        loadOptions={loadTaxes}
        onChange={onChange}
        isDisabled={disabled}
      />
    );
  }

  return (
    <Select
      inPopup
      isMulti
      value={value}
      options={options}
      onChange={onChange}
      isDisabled={disabled}
    />
  );
}

export default TaxAmountSelect;
